import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { navigation } from 'app/navigation/navigation';
import { CustomerAccount } from 'app/main/pages/authentication/shared/model/user.model';
import { LoginService } from 'app/main/pages/authentication/login/login.service';
import { NavigationService } from 'app/navigation.service';
import { ToolbarService } from './toolbar.service';
import { environment } from 'environments/environment';
import { LocalStorageService } from 'app/services/local-storage.service';
import { checkByConfigAndRoleDoppler } from 'app/shared/util/common-functions';
import { FrontConfiguration } from 'app/config/front-configuration';
import { AuthRoles } from 'app/main/pages/authentication/shared/model/enum/auth-roles.enum';
import { BrowserNotificationService } from 'app/services/browser-notification.service';

const META_PERMISSION_REQUEST_ENABLED = environment.META_PERMISSION_REQUEST_ENABLED;

@Component({
  selector: 'toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ToolbarComponent implements OnInit, OnDestroy {
  horizontalNavbar: boolean;
  rightNavbar: boolean;
  hiddenNavbar: boolean;
  languages: any;
  navigation: any;
  selectedLanguage: any;
  userStatusOptions: any[];

  user: CustomerAccount = new CustomerAccount();
  currentUser: CustomerAccount;
  dateNow = Date.now();
  interval: any;
  enableNotify = new FormControl(false);

  isToolbarEnabled: boolean = false; // Initial value to only be displayed if the associated configuration is present
  hideLangList = !META_PERMISSION_REQUEST_ENABLED;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {FuseConfigService} _fuseConfigService
   * @param {FuseSidebarService} _fuseSidebarService
   * @param {TranslateService} _translateService
   */
  constructor(
    private _fuseConfigService: FuseConfigService,
    private _fuseSidebarService: FuseSidebarService,
    private _translateService: TranslateService,
    private router: Router,
    private loginService: LoginService,
    private navigationService: NavigationService,
    private _toolbarService: ToolbarService,
    private _localStorageService: LocalStorageService,
    private browserNotificationService: BrowserNotificationService
  ) {
    // Set the defaults
    this.userStatusOptions = [
      {
        title: 'Online',
        icon: 'icon-checkbox-marked-circle',
        color: '#4CAF50',
      },
      {
        title: 'Away',
        icon: 'icon-clock',
        color: '#FFC107',
      },
      {
        title: 'Do not Disturb',
        icon: 'icon-minus-circle',
        color: '#F44336',
      },
      {
        title: 'Invisible',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#BDBDBD',
      },
      {
        title: 'Offline',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#616161',
      },
    ];

    this.languages = [
      {
        id: 'es',
        title: 'Spanish',
        flag: 'es',
      },
      {
        id: 'en',
        title: 'English',
        flag: 'us',
      },
    ];

    this.navigation = navigation;

    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this.getUser();

    this.interval = setInterval(() => {
      this.dateNow = Date.now();
    }, 1000);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to the config changes
    this._fuseConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(settings => {
        this.horizontalNavbar = settings.layout.navbar.position === 'top';
        this.rightNavbar = settings.layout.navbar.position === 'right';
        this.hiddenNavbar = settings.layout.navbar.hidden === true;
      });

    // Set the selected language from default languages
    this.selectedLanguage = _.find(this.languages, {
      id: this._translateService.currentLang,
    });

    this.navigationService.onProfileUpdated
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.getUser();
        this.setToolbarVisibility();
      });

    this.setToolbarVisibility();

    this.browserNotificationService.syncToggleControl(this.enableNotify);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    // Clean setInterval
    clearInterval(this.interval);
    this._toolbarService.enableNotifyCallSource.next(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebarOpen(key): void {
    this._fuseSidebarService.getSidebar(key).toggleOpen();
  }

  /**
   * Search
   *
   * @param value
   */
  search(value): void {
    // Do your search here...
  }

  /**
   * Set the language
   *
   * @param lang
   */
  setLanguage(lang): void {
    // Set the selected language for the toolbar
    this.selectedLanguage = lang;
    const currentUser = this._localStorageService.getCurrentUser();
    currentUser.langKey = this.selectedLanguage.id;
    this._localStorageService.setCurrentUser(currentUser);
    // Use the selected language for translations
    this._translateService.use(lang.id);
  }

  getUser(): void {
    this.currentUser = this._localStorageService.getCurrentUser();
    if (this.currentUser) {
      this.user.lastName = this.currentUser.lastName;
      this.user.firstName = this.currentUser.firstName;
      this.user.email = this.currentUser.firstName;
    }
  }

  private setToolbarVisibility(): void {
    this.isToolbarEnabled = checkByConfigAndRoleDoppler(
      [FrontConfiguration.PARTNER_TOOLBAR_ENABLED],
      AuthRoles.ROLE_ATC
    );
  }

  get showMyProfileButton(): boolean {
    const authorities: string[] = this.currentUser
      ? this.currentUser.authorities || []
      : [];
    return !authorities.includes(AuthRoles.ROLE_ATC);
  }

  logout(): void {
    this.loginService.logout();
    this.router.navigate(['/pages/auth/login']);
  }

  toggleNotifications(enabled: boolean): void {
    this.browserNotificationService.toggleNotificationControl(
      this.enableNotify,
      enabled,
      value => this._toolbarService.enableNotifyCallSource.next(value)
    );
  }

  get showBrowserNotifications(): boolean {
    return this.browserNotificationService.shouldHandleNotifications();
  }
}
